import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { CustomDay, CustomDayStatus, CustomPunchRow, groupByDate, HH_MM_RE, isDayEdited, PUNCH_TYPES } from './punchPlan';
import { PlannedPunch, PunchType } from '../types';

const SKIP_REASON: Record<Exclude<CustomDayStatus, 'planned'>, string> = {
  'skipped-weekend': 'skipped — weekend',
  'skipped-has-punches': 'skipped — already has punches'
};

/** `Mon 2026-09-14`, the weekday computed in UTC so it never depends on the viewer's local time zone. */
export const formatDayLabel = (date: string): string => {
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
  return `${weekday} ${date}`;
};

interface CustomPlanEditorProps {
  days: CustomDay[];
  plan: PlannedPunch[];
  rows: CustomPunchRow[];
  onChange: (plan: PlannedPunch[]) => void;
}

/** Defaults to expanded for every edited day and the first planned day; everything else starts collapsed. Computed once on mount so it does not fight the user's own expand/collapse clicks. */
const defaultExpanded = (days: CustomDay[], grouped: Map<string, PlannedPunch[]>, rows: CustomPunchRow[]): Set<string> => {
  const firstPlannedDay = days.find((d) => d.status === 'planned')?.date;
  const initial = new Set<string>();
  for (const day of days) {
    if (day.status !== 'planned') continue;
    const edited = isDayEdited(grouped.get(day.date) ?? [], rows);
    if (edited || day.date === firstPlannedDay) initial.add(day.date);
  }
  return initial;
};

export const CustomPlanEditor = ({ days, plan, rows, onChange }: CustomPlanEditorProps) => {
  const grouped = groupByDate(plan);
  const [expanded, setExpanded] = useState<Set<string>>(() => defaultExpanded(days, grouped, rows));

  const toggle = (date: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  const updateLine = (target: PlannedPunch, patch: Partial<PlannedPunch>) => onChange(plan.map((p) => (p === target ? { ...p, ...patch } : p)));
  const removeLine = (target: PlannedPunch) => onChange(plan.filter((p) => p !== target));
  const addLine = (date: string) => onChange([...plan, { date, time: '09:00', punchType: 'entry' }]);

  return (
    <Stack spacing={1}>
      {days.map((day) => {
        const lines = grouped.get(day.date) ?? [];
        const skipped = day.status !== 'planned';
        const edited = !skipped && isDayEdited(lines, rows);
        return (
          <Accordion key={day.date} expanded={!skipped && expanded.has(day.date)} onChange={() => toggle(day.date)} disabled={skipped} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: 'monospace' }}>{formatDayLabel(day.date)}</Typography>
                {skipped ? (
                  <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
                    {SKIP_REASON[day.status as Exclude<CustomDayStatus, 'planned'>]}
                  </Typography>
                ) : (
                  <Typography color="text.secondary" sx={{ ml: 'auto' }}>
                    {lines.length} punches{edited ? ' · edited' : ''}
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            {!skipped && (
              <AccordionDetails>
                <Stack spacing={1}>
                  {lines.map((line, index) => (
                    <Stack direction="row" spacing={1} alignItems="center" key={index}>
                      <TextField label="Time" type="time" size="small" value={line.time} error={!HH_MM_RE.test(line.time)} onChange={(e) => updateLine(line, { time: e.target.value })} InputLabelProps={{ shrink: true }} />
                      <TextField label="Type" select size="small" value={line.punchType} onChange={(e) => updateLine(line, { punchType: e.target.value as PunchType })} sx={{ minWidth: 140 }}>
                        {PUNCH_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton aria-label="Remove punch" onClick={() => removeLine(line)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addLine(day.date)}>
                    Add punch
                  </Button>
                </Stack>
              </AccordionDetails>
            )}
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default CustomPlanEditor;
